import { useMemo, useState, type ReactNode } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  FileText,
  Loader2,
  Mail,
  Printer,
  Search,
  Send,
  XCircle,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import {
  useBillingRunDryRun,
  useBillingRunProgress,
  useBillingRunStart,
  useEnabledStatementCustomers,
  type BillingAudience,
  type BillingChannel,
  type BillingDocType,
  type BillingRunDryRunResult,
} from '@/hooks/admin/useBillingRun';

/**
 * AdminBilling — bulk "Billing Run" console. Sends open-items statements to
 * customers: email to all statement-enabled, paper (DocuPost) to a hand-picked
 * set. Safety rails: dry-run preview is mandatory before a real run; paper is
 * hand-pick only (UI guards + backend rejects paper+all); the confirm dialog
 * repeats the paper count + estimated postage before spending money.
 */

function fmtUsd(n: number): string {
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

const DOC_TYPE_LABEL: Record<BillingDocType, string> = {
  statement: 'Statement',
  invoice: 'Invoices',
  both: 'Both',
};

/** One-line "3 statement · 2 invoices · 1 both" summary for the confirm dialog. */
function docTypeSummary(counts: Partial<Record<BillingDocType, number>>): string {
  const parts = (['statement', 'invoice', 'both'] as BillingDocType[])
    .filter((k) => (counts[k] ?? 0) > 0)
    .map((k) => `${counts[k]} ${DOC_TYPE_LABEL[k].toLowerCase()}`);
  return parts.length > 0 ? parts.join(' · ') : '—';
}

/** Pill row showing the doc-type breakdown across the resolved recipients. */
function DocTypeBreakdown({ counts }: { counts: Partial<Record<BillingDocType, number>> }) {
  const entries = (['statement', 'invoice', 'both'] as BillingDocType[]).filter(
    (k) => (counts[k] ?? 0) > 0,
  );
  if (entries.length === 0) return null;
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-[10px] uppercase tracking-tight text-brix-muted font-semibold">
        Documents
      </span>
      {entries.map((k) => (
        <span
          key={k}
          className="inline-flex items-center gap-1 rounded-md border border-brix-border bg-brix-surface-alt px-2 py-0.5 text-xs text-brix-ink"
        >
          <span className="font-semibold">{counts[k]}</span> {DOC_TYPE_LABEL[k]}
        </span>
      ))}
    </div>
  );
}

const CHANNELS: { key: BillingChannel; label: string; icon: typeof Mail; desc: string }[] = [
  { key: 'email', label: 'Email', icon: Mail, desc: 'E-statement to statement-enabled customers' },
  { key: 'paper', label: 'Paper', icon: Printer, desc: 'Mailed via DocuPost — real postage' },
  { key: 'both', label: 'Both', icon: FileText, desc: 'Email + paper where eligible' },
];

export default function AdminBilling() {
  const [channel, setChannel] = useState<BillingChannel>('email');
  const [audience, setAudience] = useState<BillingAudience>('all');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [preview, setPreview] = useState<BillingRunDryRunResult | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [runId, setRunId] = useState<string | null>(null);

  const wantsPaper = channel === 'paper' || channel === 'both';
  // Paper audiences: 'selected' (hand-pick) or 'paper_opted_in'. Email-only
  // audiences: 'all' (statement-enabled) or 'selected'. When we cross between
  // the two, fall back to a valid default for the channel.
  const effectiveAudience: BillingAudience = wantsPaper
    ? audience === 'paper_opted_in' || audience === 'selected'
      ? audience
      : 'selected'
    : audience === 'all' || audience === 'selected'
      ? audience
      : 'all';
  const needsPicker = effectiveAudience === 'selected';

  const customers = useEnabledStatementCustomers();
  const dryRun = useBillingRunDryRun();
  const start = useBillingRunStart();
  const progress = useBillingRunProgress(runId);

  const paperOptedInCount = useMemo(
    () => (customers.data ?? []).filter((c) => c.paper_statements_enabled).length,
    [customers.data],
  );

  const filtered = useMemo(() => {
    const all = customers.data ?? [];
    const q = search.trim().toLowerCase();
    if (!q) return all;
    return all.filter((c) => c.name.toLowerCase().includes(q));
  }, [customers.data, search]);

  const selectedCount = selectedIds.size;

  function toggle(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    setPreview(null);
  }

  function resetPreview() {
    setPreview(null);
  }

  const canPreview =
    !needsPicker || selectedCount > 0;

  function handlePreview() {
    const ids = Array.from(selectedIds);
    dryRun.mutate(
      {
        channel,
        audience: effectiveAudience,
        customer_ids: needsPicker ? ids : undefined,
      },
      { onSuccess: (res) => setPreview(res) },
    );
  }

  function handleConfirmRun() {
    const ids = Array.from(selectedIds);
    start.mutate(
      {
        channel,
        audience: effectiveAudience,
        customer_ids: needsPicker ? ids : undefined,
      },
      {
        onSuccess: (res) => {
          setConfirmOpen(false);
          setRunId(res.run_id);
        },
      },
    );
  }

  const runRow = progress.data?.run ?? null;
  const running = runRow && (runRow.status === 'pending' || runRow.status === 'running');
  const done = runRow && runRow.status === 'done';
  const errored = runRow && runRow.status === 'error';

  return (
    <div className="px-6 py-6 space-y-6">
      <div>
        <h2 className="font-display text-lg font-semibold text-brix-ink">Billing Run</h2>
        <p className="text-sm text-brix-muted mt-1 max-w-2xl">
          Bulk-send billing documents. Each customer gets the document their profile chooses —
          a <span className="font-semibold">statement</span>, their open{' '}
          <span className="font-semibold">invoices</span>, or both. Email goes to enabled customers;
          paper is hand-picked and costs real postage. Preview first — nothing sends until you
          confirm.
        </p>
      </div>

      {/* ---- Channel ---- */}
      <section className="space-y-2">
        <div className="text-[11px] font-semibold text-brix-muted uppercase tracking-tight">
          Channel
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
          {CHANNELS.map(({ key, label, icon: Icon, desc }) => {
            const active = channel === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => {
                  setChannel(key);
                  resetPreview();
                }}
                className={cn(
                  'text-left rounded-xl border px-4 py-3 cursor-pointer transition-all',
                  active
                    ? 'border-brix-primary bg-brix-primary-tint'
                    : 'border-brix-border bg-brix-surface hover:border-brix-primary/40',
                )}
              >
                <div className="flex items-center gap-2 text-sm font-semibold text-brix-ink">
                  <Icon size={15} className="text-brix-primary" />
                  {label}
                </div>
                <div className="text-[11px] text-brix-muted mt-1">{desc}</div>
              </button>
            );
          })}
        </div>
      </section>

      {/* ---- Audience ---- */}
      <section className="space-y-2">
        <div className="text-[11px] font-semibold text-brix-muted uppercase tracking-tight">
          Audience
        </div>
        <div className="flex gap-2 flex-wrap">
          {wantsPaper ? (
            <>
              <AudienceButton
                active={effectiveAudience === 'selected'}
                onClick={() => {
                  setAudience('selected');
                  resetPreview();
                }}
              >
                Hand-pick customers
              </AudienceButton>
              <AudienceButton
                active={effectiveAudience === 'paper_opted_in'}
                onClick={() => {
                  setAudience('paper_opted_in');
                  resetPreview();
                }}
              >
                Everyone who opted into paper ({paperOptedInCount})
              </AudienceButton>
            </>
          ) : (
            <>
              <AudienceButton
                active={effectiveAudience === 'all'}
                onClick={() => {
                  setAudience('all');
                  resetPreview();
                }}
              >
                All statement-enabled
              </AudienceButton>
              <AudienceButton
                active={effectiveAudience === 'selected'}
                onClick={() => {
                  setAudience('selected');
                  resetPreview();
                }}
              >
                Hand-pick customers
              </AudienceButton>
            </>
          )}
        </div>
        {wantsPaper && (
          <p className="text-[11px] text-amber-700 flex items-center gap-1.5">
            <AlertTriangle size={12} />
            Paper never mass-mails to everyone — pick specific customers, or mail only those who
            opted into paper.
          </p>
        )}
      </section>

      {/* ---- Hand-pick multi-select ---- */}
      {needsPicker && (
        <section className="space-y-2">
          <div className="flex items-center justify-between gap-3">
            <div className="text-[11px] font-semibold text-brix-muted uppercase tracking-tight">
              Customers ({selectedCount} selected)
            </div>
            {selectedCount > 0 && (
              <button
                type="button"
                onClick={() => {
                  setSelectedIds(new Set());
                  resetPreview();
                }}
                className="text-[11px] text-brix-primary hover:underline cursor-pointer"
              >
                Clear
              </button>
            )}
          </div>
          <div className="relative">
            <Search
              size={14}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-brix-muted pointer-events-none"
            />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search customers…"
              className="pl-9"
            />
          </div>
          <div className="rounded-xl border border-brix-border bg-brix-surface max-h-72 overflow-auto divide-y divide-brix-border">
            {customers.isLoading ? (
              <div className="px-4 py-6 text-sm text-brix-muted flex items-center gap-2">
                <Loader2 size={14} className="animate-spin" /> Loading customers…
              </div>
            ) : filtered.length === 0 ? (
              <div className="px-4 py-6 text-sm text-brix-muted">No matching customers.</div>
            ) : (
              filtered.map((c) => {
                const checked = selectedIds.has(c.id);
                return (
                  <label
                    key={c.id}
                    className="flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-brix-primary-tint/50"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggle(c.id)}
                      className="accent-brix-primary cursor-pointer"
                    />
                    <span className="text-sm text-brix-ink flex-1 min-w-0 truncate">{c.name}</span>
                    {!c.statements_enabled && (
                      <span
                        className="text-[10px] text-amber-700 shrink-0"
                        title="Not statement-enabled — will be skipped on the email channel"
                      >
                        no e-statement
                      </span>
                    )}
                  </label>
                );
              })
            )}
          </div>
        </section>
      )}

      {/* ---- Preview ---- */}
      <section className="space-y-3">
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="secondary"
            size="sm"
            onClick={handlePreview}
            disabled={!canPreview || dryRun.isPending}
          >
            {dryRun.isPending ? <Loader2 size={12} className="animate-spin" /> : <Search size={12} />}
            {dryRun.isPending ? 'Estimating…' : 'Preview run'}
          </Button>
          {dryRun.error && (
            <span className="text-xs text-red-600">{dryRun.error.message}</span>
          )}
        </div>

        {preview && (
          <div className="rounded-xl border border-brix-border bg-brix-surface px-4 py-4 space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <Stat label="Recipients" value={String(preview.recipients.length)} />
              <Stat label="Email" value={String(preview.email_count)} />
              <Stat label="Paper letters" value={String(preview.paper_letter_count)} />
            </div>
            <DocTypeBreakdown counts={preview.doc_type_counts} />
            {preview.paper_letter_count > 0 && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5 text-sm text-amber-900 flex items-start gap-2">
                <AlertTriangle size={15} className="mt-0.5 shrink-0" />
                <div>
                  <span className="font-semibold">
                    {preview.paper_letter_count} paper{' '}
                    {preview.paper_letter_count === 1 ? 'letter' : 'letters'} to{' '}
                    {preview.paper_count} {preview.paper_count === 1 ? 'customer' : 'customers'} ·{' '}
                    {fmtUsd(preview.est_cost)} est. postage.
                  </span>{' '}
                  One letter per statement and one per open invoice. Paper means real postage spent
                  via DocuPost. This is an estimate; DocuPost's actual per-piece price applies.
                </div>
              </div>
            )}
            {preview.recipients.length === 0 ? (
              <p className="text-sm text-brix-muted">
                No eligible recipients. (Email needs active + statement-enabled customers; paper needs
                a valid mailing address.)
              </p>
            ) : (
              <Button
                variant="primary"
                size="sm"
                onClick={() => setConfirmOpen(true)}
                disabled={!!running}
              >
                <Send size={12} /> Confirm &amp; run
              </Button>
            )}
          </div>
        )}
      </section>

      {/* ---- Live progress ---- */}
      {runId && runRow && (
        <section className="rounded-xl border border-brix-border bg-brix-surface px-4 py-4 space-y-3">
          <div className="flex items-center gap-2">
            {running && <Loader2 size={15} className="animate-spin text-brix-primary" />}
            {done && <CheckCircle2 size={15} className="text-emerald-600" />}
            {errored && <XCircle size={15} className="text-red-600" />}
            <span className="text-sm font-semibold text-brix-ink">
              {running ? 'Run in progress…' : done ? 'Run complete' : 'Run errored'}
            </span>
          </div>
          <div className="grid grid-cols-4 gap-3">
            <Stat label="Total" value={String(runRow.total)} />
            <Stat label="Email sent" value={String(runRow.sent_email)} />
            <Stat label="Paper sent" value={String(runRow.sent_paper)} />
            <Stat label="Failed" value={String(runRow.failed)} tone={runRow.failed > 0 ? 'bad' : undefined} />
          </div>
          {errored && runRow.error && (
            <p className="text-xs text-red-600">{runRow.error}</p>
          )}
          {(progress.data?.items.length ?? 0) > 0 && (
            <details className="text-xs text-brix-muted">
              <summary className="cursor-pointer select-none">Per-recipient results</summary>
              <div className="mt-2 max-h-60 overflow-auto divide-y divide-brix-border rounded-lg border border-brix-border">
                {progress.data!.items.map((it) => (
                  <div key={it.id} className="flex items-center gap-2 px-3 py-1.5">
                    <span className="w-12 shrink-0 capitalize">{it.channel}</span>
                    <span
                      className={cn(
                        'w-16 shrink-0 capitalize font-semibold',
                        it.status === 'sent'
                          ? 'text-emerald-700'
                          : it.status === 'error'
                            ? 'text-red-600'
                            : 'text-brix-muted',
                      )}
                    >
                      {it.status}
                    </span>
                    <span className="flex-1 min-w-0 truncate font-mono text-[10px]">
                      {it.customer_id ?? '—'}
                    </span>
                    {it.error && <span className="text-red-500 truncate max-w-[40%]">{it.error}</span>}
                  </div>
                ))}
              </div>
            </details>
          )}
        </section>
      )}

      {/* ---- Confirm dialog ---- */}
      {confirmOpen && preview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-md rounded-2xl border border-brix-border bg-brix-surface p-6 shadow-card-hover space-y-4">
            <div className="flex items-center gap-2">
              <Send size={16} className="text-brix-primary" />
              <h3 className="font-display text-base font-semibold text-brix-ink">
                Confirm billing run
              </h3>
            </div>
            <div className="text-sm text-brix-ink space-y-2">
              <p>
                You're about to send statements to{' '}
                <span className="font-semibold">{preview.recipients.length}</span>{' '}
                {preview.recipients.length === 1 ? 'customer' : 'customers'}.
              </p>
              <ul className="text-sm text-brix-muted space-y-1">
                <li>
                  Email: <span className="font-semibold text-brix-ink">{preview.email_count}</span>
                </li>
                <li>
                  Paper letters:{' '}
                  <span className="font-semibold text-brix-ink">{preview.paper_letter_count}</span>{' '}
                  (to {preview.paper_count} {preview.paper_count === 1 ? 'customer' : 'customers'})
                </li>
                <li>
                  Documents:{' '}
                  <span className="font-semibold text-brix-ink">{docTypeSummary(preview.doc_type_counts)}</span>
                </li>
              </ul>
              {preview.paper_letter_count > 0 && (
                <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5 text-sm text-amber-900 flex items-start gap-2">
                  <AlertTriangle size={15} className="mt-0.5 shrink-0" />
                  <span>
                    <span className="font-semibold">
                      {preview.paper_letter_count} paper{' '}
                      {preview.paper_letter_count === 1 ? 'letter' : 'letters'} ·{' '}
                      {fmtUsd(preview.est_cost)} est. postage
                    </span>{' '}
                    will be mailed via DocuPost. This spends real money and cannot be undone.
                  </span>
                </div>
              )}
            </div>
            {start.error && <p className="text-xs text-red-600">{start.error.message}</p>}
            <div className="flex items-center justify-end gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setConfirmOpen(false)}
                disabled={start.isPending}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleConfirmRun}
                disabled={start.isPending}
                className={preview.paper_count > 0 ? 'bg-red-600 hover:bg-red-700 text-red-50' : undefined}
              >
                {start.isPending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                {start.isPending ? 'Starting…' : preview.paper_count > 0 ? 'Send for real' : 'Run now'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AudienceButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded-lg border px-3.5 py-2 text-sm cursor-pointer transition-all',
        active
          ? 'border-brix-primary bg-brix-primary-tint text-brix-primary font-semibold'
          : 'border-brix-border bg-brix-surface text-brix-ink hover:border-brix-primary/40',
      )}
    >
      {children}
    </button>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: 'bad' }) {
  return (
    <div className="rounded-lg border border-brix-border bg-brix-surface-alt px-3 py-2">
      <div className="text-[10px] uppercase tracking-tight text-brix-muted font-semibold">{label}</div>
      <div className={cn('text-xl font-display font-semibold', tone === 'bad' ? 'text-red-600' : 'text-brix-ink')}>
        {value}
      </div>
    </div>
  );
}
